import { AlertColor, Avatar, Box, Typography } from '@mui/material';
import { CountryCode } from 'libphonenumber-js';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import strings from '../../../common/Translation/Translate';
import Notification from '../../../components/Notification';
import PageLoader from '../../../components/PageLoader';
import { FormDataModel } from '../../../components/Form';
import axiosInstance from '../../../utils/axios';
import { getTableAvatarSx } from '../../../utils/avatarColors';
import { privileges } from '../../../utils/constants/privileges';
import { URLS } from '../../../utils/constants/urls';
import { images } from '../../../utils/constants/images';
import { Privilege } from '../../../utils/redux/reducer/authentication-slice';
import { filterDataforApi, pageNo, rowCount } from '../../../utils/redux/reducer/filter-slice';
import { fetchFromStorage } from '../../../utils/storage';
import ListPage from '../CommonPage';
import MasterDataEntityDrawer from '../CommonPage/MasterDataEntityDrawer';
import { lastUpdatedTableColumn } from '../CommonPage/lastUpdatedColumn';
import { usageTableColumn } from '../CommonPage/usageColumn';
import { AddAgentsForm } from './AddEditAgents/AddAgentsForm';

const getSourceInitials = (name?: string | null) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const AgentList: FC = () => {
  const index = useSelector(pageNo);
  const filter = useSelector(filterDataforApi);
  const pageSize = useSelector(rowCount);
  const userPrivileges = useSelector(Privilege) as string[];
  const defaultCountry = (fetchFromStorage('defaultCountryCode') || 'IN') as CountryCode;

  const [pageResponse, setPageResponse] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState<FormDataModel>({});
  const [message, setMessage] = useState<{ display: boolean; severity: AlertColor | null; message: string }>({
    display: false,
    severity: null,
    message: '',
  });

  const formModel = useMemo(
    () => AddAgentsForm(strings, defaultCountry).map(field => ({ ...field, responsive: { xs: 12 } })),
    [defaultCountry],
  );

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: error?.response?.data?.error_description ?? error?.response?.data?.message ?? error?.message,
    });
  };

  const getList = useCallback(
    async (filterDataParam: any) => {
      setIsLoading(true);
      try {
        const body = filterDataParam ?? {
          page: index + 1,
          size: pageSize,
          filter: filter ?? [],
          sort: ['createdAt:desc'],
        };
        const { data } = await axiosInstance.post(URLS.agents, body);
        setPageResponse(data);
      } catch (error: any) {
        handleCatch(error);
      } finally {
        setIsLoading(false);
      }
    },
    [index, pageSize, filter],
  );

  useEffect(() => {
    getList(null);
  }, [getList]);

  const openDrawer = useCallback(async (recordId: number | null) => {
    setEditingId(recordId);
    if (recordId) {
      try {
        setDrawerLoading(true);
        const { data } = await axiosInstance.get(URLS.editAgent(String(recordId)));
        setFormValues({
          name: data?.name ?? '',
          email: data?.email ?? '',
          phone: data?.phone ?? {},
        });
      } catch (error: any) {
        handleCatch(error);
        return;
      } finally {
        setDrawerLoading(false);
      }
    } else {
      setFormValues({ name: '', email: '', phone: {} });
    }
    setDrawerOpen(true);
  }, []);

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingId(null);
    setFormValues({});
  };

  const handleSave = async (body: FormDataModel) => {
    try {
      setSaving(true);
      const payload = {
        ...body,
        phone:
          body.phone === '' || body.phone == null
            ? { countryCode: null, value: '' }
            : body.phone,
      };
      if (editingId) {
        await axiosInstance.put(URLS.editAgent(String(editingId)), payload);
      } else {
        await axiosInstance.post(URLS.addAgent, payload);
      }
      setMessage({
        display: true,
        severity: 'success',
        message: editingId ? strings.common_update_message : strings.common_save_message,
      });
      closeDrawer();
      getList(null);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingId) return;
    try {
      setSaving(true);
      await axiosInstance.delete(URLS.deleteAgent(String(editingId)));
      setMessage({ display: true, severity: 'success', message: strings.common_delete_message });
      closeDrawer();
      getList(null);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = useCallback(async (id: number) => {
    try {
      setIsLoading(true);
      await axiosInstance.put(URLS.toggleAgent(id));
      await getList(null);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  }, [getList]);

  const pageConfig = useMemo(
    () => ({
      title: 'Sources',
      entityLabel: 'Sources',
      subtitle: strings.source_list_subtitle,
      searchField: 'search',
      searchPlaceholder: 'Search sources',
      AddButtonText: strings.source_button_add,
      listApi: URLS.agents,
      deleteApi: URLS.deleteAgent,
      addPrivilege: privileges.add_agent,
      editPrivilege: privileges.edit_agent,
      deletePrivilege: privileges.delete_agent,
      customAdd: () => openDrawer(null),
      customEdit: (id: number) => openDrawer(id),
      tableColumn: [
        {
          field: 'name',
          headerName: strings.agent_column_name,
          flex: 2,
          sortable: true,
          renderCell: (_value: any, row: any) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={getTableAvatarSx(row.name)}>
                {getSourceInitials(row.name)}
              </Avatar>
              <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>{row.name || '—'}</Typography>
            </Box>
          ),
        },
        {
          field: 'email',
          headerName: 'Contact',
          flex: 2,
          sortable: true,
          renderCell: (_value: any, row: any) => {
            const email = row.email;
            const phone = row.phoneNo || row.phone?.value || '';
            if (!email && !phone) return '—';
            return (
              <>
                {email ? <Typography sx={{ fontSize: '0.8125rem', lineHeight: 1.35 }}>{email}</Typography> : null}
                {phone ? (
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.35 }}>
                    {String(phone).replace(/\D/g, '') || phone}
                  </Typography>
                ) : null}
              </>
            );
          },
        },
        {
          field: 'active',
          headerName: strings.agent_column_status,
          flex: 0.9,
          sortable: false,
          renderCell: (_value: any, row: any) => (
            <Box
              component="img"
              src={row?.active ? images.activeToggleIcon : images.inActiveToggleIcon}
              alt="toggle source status"
              sx={{ width: 20, height: 20, cursor: 'pointer' }}
              onClick={event => {
                event.stopPropagation();
                handleToggle(row.id);
              }}
            />
          ),
        },
        usageTableColumn(row => [
          { count: row.bookingCount, singular: 'Booking', plural: 'Bookings', muted: false },
        ]),
        lastUpdatedTableColumn(),
      ],
    }),
    [openDrawer, handleToggle],
  );

  return (
    <>
      {isLoading && <PageLoader />}
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={2000}
        />
      )}
      <ListPage isLoading={isLoading} pageConfig={pageConfig} pageResponse={pageResponse} getList={getList} />
      <MasterDataEntityDrawer
        open={drawerOpen}
        title={editingId ? strings.source_title_edit : strings.source_title_add}
        isEdit={Boolean(editingId)}
        formModel={formModel}
        formValues={formValues}
        loading={drawerLoading}
        saving={saving}
        canDelete={userPrivileges?.includes(privileges.delete_agent)}
        deleteEntityName={String(formValues.name || '')}
        onClose={closeDrawer}
        onSave={handleSave}
        onDelete={handleDelete}
        onInvalid={msg => setMessage({ display: true, severity: 'error', message: msg })}
      />
    </>
  );
};

export default AgentList;
